import React, { useState, useEffect } from 'react';
import { ArrowRightLeft, ArrowRight } from 'lucide-react';

const Calculator: React.FC = () => {
  // Tabs: 'MMK_TO_THB' (Give Kyat, Take Baht) | 'THB_TO_MMK' (Give Baht, Take Kyat)
  const [activeTab, setActiveTab] = useState<'MMK_TO_THB' | 'THB_TO_MMK'>('MMK_TO_THB');
  
  // Inputs (Clean strings without commas for logic)
  const [marketPrice, setMarketPrice] = useState<string>(''); // THB amount for 100k MMK
  const [exchangeRate, setExchangeRate] = useState<string>(''); // 1 THB = ? MMK
  
  // Amounts
  const [inputAmount, setInputAmount] = useState<string>('100000'); // Top Box (Default set to 100,000)
  const [resultAmount, setResultAmount] = useState<string>(''); // Bottom Box (Calculated)
  
  // Track which field was last edited to maintain calculation direction
  const [lastEdited, setLastEdited] = useState<'input' | 'result'>('input');

  // Constants
  const BASE_MMK = 100000;

  // Formatting helpers
  const parseVal = (str: string) => parseFloat(str.replace(/,/g, '')) || 0;
  
  const formatNumber = (val: string | number) => {
    if (val === undefined || val === null || val === '') return '';
    const str = val.toString().replace(/,/g, '');
    const parts = str.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  // Flag URLs
  const THAI_FLAG = "https://flagcdn.com/w40/th.png";
  const MYANMAR_FLAG = "https://flagcdn.com/w40/mm.png";

  // --- Calculation Logic Helpers ---

  const calculateOutputFromInput = (inVal: number, price: number, tab: string) => {
    if (!price || price === 0) return 0;
    if (tab === 'MMK_TO_THB') {
      return (inVal / BASE_MMK) * price;
    } else {
      return (inVal / price) * BASE_MMK;
    }
  };

  const calculateInputFromOutput = (outVal: number, price: number, tab: string) => {
    if (!price || price === 0) return 0;
    if (tab === 'MMK_TO_THB') {
      return (outVal / price) * BASE_MMK;
    } else {
      return (outVal / BASE_MMK) * price;
    }
  };

  // --- Effects to sync when Price or Tab changes ---

  useEffect(() => {
    const priceVal = parseVal(marketPrice);
    const inVal = parseVal(inputAmount);
    const outVal = parseVal(resultAmount);

    if (priceVal > 0) {
      if (lastEdited === 'input') {
        const newResult = calculateOutputFromInput(inVal, priceVal, activeTab);
        setResultAmount(newResult === 0 ? '' : newResult.toFixed(2));
      } else {
        const newInput = calculateInputFromOutput(outVal, priceVal, activeTab);
        setInputAmount(newInput === 0 ? '' : newInput.toFixed(2));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketPrice, activeTab]); 

  // --- Handlers ---

  const handleMarketPriceChange = (val: string) => {
    const clean = val.replace(/[^0-9.]/g, '');
    setMarketPrice(clean);

    const price = parseFloat(clean);
    if (price > 0) {
      const rate = BASE_MMK / price;
      setExchangeRate(Number.isInteger(rate) ? rate.toString() : rate.toFixed(2));
      
      if (lastEdited === 'input') {
        const inVal = parseVal(inputAmount);
        const newResult = calculateOutputFromInput(inVal, price, activeTab);
        setResultAmount(newResult === 0 ? '' : newResult.toFixed(2));
      } else {
        const outVal = parseVal(resultAmount);
        const newInput = calculateInputFromOutput(outVal, price, activeTab);
        setInputAmount(newInput === 0 ? '' : newInput.toFixed(2));
      }
    } else {
      setExchangeRate('');
    }
  };

  const handleExchangeRateChange = (val: string) => {
    const clean = val.replace(/[^0-9.]/g, '');
    setExchangeRate(clean);

    const rate = parseFloat(clean);
    if (rate > 0) {
      const price = BASE_MMK / rate;
      setMarketPrice(Number.isInteger(price) ? price.toString() : price.toFixed(2));
      
      if (lastEdited === 'input') {
        const inVal = parseVal(inputAmount);
        const newResult = calculateOutputFromInput(inVal, price, activeTab);
        setResultAmount(newResult === 0 ? '' : newResult.toFixed(2));
      } else {
        const outVal = parseVal(resultAmount);
        const newInput = calculateInputFromOutput(outVal, price, activeTab);
        setInputAmount(newInput === 0 ? '' : newInput.toFixed(2));
      }
    } else {
      setMarketPrice('');
    }
  };

  const handleInputAmountChange = (val: string) => {
    const clean = val.replace(/[^0-9.]/g, '');
    setInputAmount(clean);
    setLastEdited('input');

    const priceVal = parseVal(marketPrice);
    const inVal = parseFloat(clean);

    if (priceVal > 0 && !isNaN(inVal)) {
      const newResult = calculateOutputFromInput(inVal, priceVal, activeTab);
      setResultAmount(newResult === 0 ? '' : newResult.toFixed(2));
    }
  };

  const handleResultAmountChange = (val: string) => {
    const clean = val.replace(/[^0-9.]/g, '');
    setResultAmount(clean);
    setLastEdited('result');

    const priceVal = parseVal(marketPrice);
    const outVal = parseFloat(clean);

    if (priceVal > 0 && !isNaN(outVal)) {
      const newInput = calculateInputFromOutput(outVal, priceVal, activeTab);
      setInputAmount(newInput === 0 ? '' : newInput.toFixed(0));
    }
  };

  const handleTabSwitch = (tab: 'MMK_TO_THB' | 'THB_TO_MMK') => {
    setActiveTab(tab);
    setLastEdited('input');
  };

  const activeBorderClass = activeTab === 'MMK_TO_THB' ? 'focus:ring-blue-100' : 'focus:ring-emerald-100';

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 p-4 mx-4 mb-4 transition-colors duration-300">
      
      {/* Tabs with Icon in Middle */}
      <div className="relative flex items-center bg-gray-100 dark:bg-slate-700 p-1 rounded-full mb-6">
        <button
          onClick={() => handleTabSwitch('MMK_TO_THB')}
          className={`flex-1 py-2.5 rounded-full text-xs sm:text-sm font-bold font-myanmar transition-all z-10 ${
            activeTab === 'MMK_TO_THB'
              ? 'bg-blue-600 text-white shadow-md transform scale-105'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          ကျပ်ပေး ဘတ်ယူ
        </button>

        <div className="bg-white dark:bg-slate-600 p-1.5 rounded-full shadow-sm z-20 mx-[-10px] border-2 border-gray-100 dark:border-slate-700">
           <ArrowRightLeft size={16} className="text-gray-400 dark:text-gray-300" />
        </div>

        <button
          onClick={() => handleTabSwitch('THB_TO_MMK')}
          className={`flex-1 py-2.5 rounded-full text-xs sm:text-sm font-bold font-myanmar transition-all z-10 ${
            activeTab === 'THB_TO_MMK'
              ? 'bg-emerald-600 text-white shadow-md transform scale-105'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          ဘတ်ပေး ကျပ်ယူ
        </button>
      </div>

      <div className="space-y-4">
        
        {/* Field: (Kyat) 1 Lakh Market Price */}
        <div>
          <label className="block text-red-600 dark:text-red-400 text-xs mb-1 font-myanmar font-semibold text-center">
            (ကျပ်) ၁ သိန်းရဲ့ ဘတ်ပေါက်ဈေး
          </label>
          <div className="relative">
            {!marketPrice && (
              <div className="absolute left-3 top-1/2 text-red-500 animate-slide-right pointer-events-none z-10">
                <ArrowRight size={20} strokeWidth={3} />
              </div>
            )}
            <input
              type="text"
              inputMode="decimal"
              value={formatNumber(marketPrice)}
              onChange={(e) => handleMarketPriceChange(e.target.value)}
              placeholder="ပေါက်ဈေးရိုက်ထည့်ပါ"
              className={`w-full bg-gray-50 dark:bg-slate-700 rounded-xl p-3 text-center text-gray-800 dark:text-white font-bold outline-none focus:ring-2 ${activeBorderClass} placeholder-blink input-attention-pulse font-myanmar transition-all border border-gray-200 dark:border-slate-600 text-lg relative z-0`}
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none flex items-center gap-1 opacity-50">
              <span className="text-[10px] font-bold">THB</span>
            </div>
          </div>
        </div>

        {/* Field: Exchange Rate ပေါက်ဈေး - Even more compact (20% smaller) */}
        <div className="flex flex-col items-center justify-center my-3">
          <label className="block text-gray-400 dark:text-gray-500 text-[10px] mb-1 font-myanmar">
            Exchange Rate ပေါက်ဈေး
          </label>
          <div className="flex items-center gap-2">
            <div className={`${activeTab === 'MMK_TO_THB' ? 'bg-blue-600' : 'bg-emerald-600'} rounded-full py-0.5 px-3 shadow-sm flex items-center justify-center transition-colors duration-300`}>
               <div className="flex items-center justify-center gap-1 text-white font-bold text-[10px]">
                  <span className="flex items-center gap-1">
                    <img src={THAI_FLAG} alt="THB" className="w-3 h-auto rounded-sm" /> 
                    1 THB =
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={formatNumber(exchangeRate)}
                    onChange={(e) => handleExchangeRateChange(e.target.value)}
                    placeholder="0"
                    className="bg-transparent w-12 text-center text-white outline-none placeholder-white/50 border-b border-white/30 focus:border-white transition-colors"
                  />
                  <span className="flex items-center gap-1">
                    MMK 
                    <img src={MYANMAR_FLAG} alt="MMK" className="w-3 h-auto rounded-sm" />
                  </span>
               </div>
            </div>

            {/* Buy/Sell Indicator */}
            <div className="bg-orange-500 text-white text-[9px] font-bold px-2 py-0.5 rounded flex items-center gap-1 shadow-sm uppercase">
               {activeTab === 'MMK_TO_THB' ? 'Sell Price' : 'Buy Price'}
               <div className="w-2 h-0.5 bg-white/50 rounded-full"></div>
            </div>
          </div>
        </div>
        
        {/* Input & Result Container */}
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 border border-gray-100 dark:border-slate-700 space-y-4">
            <div>
              <label className="block text-gray-500 dark:text-gray-400 text-xs mb-1 font-myanmar">
                {activeTab === 'MMK_TO_THB' ? 'သင်ပေးရမည့် ကျပ်ငွေ' : 'သင်ပေးရမည့် ဘတ်ငွေ'}
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="decimal"
                  value={formatNumber(inputAmount)}
                  onChange={(e) => handleInputAmountChange(e.target.value)}
                  placeholder="0"
                  className={`w-full bg-white dark:bg-slate-700 rounded-lg p-3 pl-4 pr-16 text-left text-gray-800 dark:text-white font-bold outline-none focus:ring-2 ${activeBorderClass} transition-colors shadow-sm border border-gray-200 dark:border-slate-600`}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none flex items-center gap-2">
                  <span className="text-gray-500 dark:text-gray-300 font-bold text-xs">
                    {activeTab === 'MMK_TO_THB' ? 'MMK' : 'THB'}
                  </span>
                  <img 
                    src={activeTab === 'MMK_TO_THB' ? MYANMAR_FLAG : THAI_FLAG} 
                    alt="Currency" 
                    className="w-5 h-auto rounded-sm shadow-sm"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-gray-500 dark:text-gray-400 text-xs mb-1 font-myanmar">
                {activeTab === 'MMK_TO_THB' ? 'သင်ရယူမည့် ဘတ်ငွေ' : 'သင်ရယူမည့် ကျပ်ငွေ'}
              </label>
              <div className="relative">
                <input
                    type="text"
                    inputMode="decimal"
                    value={formatNumber(resultAmount)}
                    onChange={(e) => handleResultAmountChange(e.target.value)}
                    placeholder="0"
                    className={`w-full bg-white dark:bg-slate-700 rounded-lg p-3 pl-4 pr-16 text-left ${activeTab === 'MMK_TO_THB' ? 'text-blue-600 dark:text-blue-400' : 'text-emerald-600 dark:text-emerald-400'} font-bold text-xl outline-none focus:ring-2 ${activeBorderClass} transition-colors shadow-sm border border-gray-200 dark:border-slate-600`}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-20 flex items-center gap-2">
                    <span className="text-gray-500 dark:text-gray-400 font-bold text-xs">
                    {activeTab === 'MMK_TO_THB' ? 'THB' : 'MMK'}
                    </span>
                    <img 
                    src={activeTab === 'MMK_TO_THB' ? THAI_FLAG : MYANMAR_FLAG} 
                    alt="Currency" 
                    className="w-5 h-auto rounded-sm shadow-sm"
                    />
                </div>
              </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default Calculator;